export interface IndexedRow { t: string; [bookKey: string]: string | number }
export interface BookSeries { key: string; points: { t: string; equity: number }[] }

/** Rebase N book series to 100 at a common base = the LATEST first-date in the
 *  set. A book is DROPPED unless it has a real, positive observation exactly AT
 *  the base date — anchoring to a later point would put its 100 at a different
 *  x-position, which is wrong-way #1 above. */
export function indexSeries(series: BookSeries[]): { rows: IndexedRow[]; base: string | null } {
  const usable = series.filter((s) => s.points.length > 0)
  if (usable.length === 0) return { rows: [], base: null }
  // Defensive sort: the §2.1 contract says oldest-first, but an unsorted series
  // would make `points[0].t` an arbitrary date and silently blank the chart.
  const sorted = usable.map((s) => ({
    key: s.key,
    points: [...s.points].sort((a, b) => (a.t < b.t ? -1 : a.t > b.t ? 1 : 0)),
  }))
  const base = sorted.map((s) => s.points[0].t).reduce((a, b) => (a >= b ? a : b))
  const byKey = new Map<string, { m: Map<string, number>; base0: number }>()
  for (const s of sorted) {
    const m = new Map(s.points.map((p) => [p.t, p.equity]))
    const base0 = m.get(base)            // AT base, never `find(p => p.t >= base)`
    if (base0 !== undefined && base0 > 0) byKey.set(s.key, { m, base0 })
  }
  if (byKey.size === 0) return { rows: [], base }
  const dates = [...new Set(sorted.flatMap((s) => s.points.map((p) => p.t)))]
    .filter((t) => t >= base)
    .sort()
  const rows = dates.map((t) => {
    const row: IndexedRow = { t }
    for (const [key, { m, base0 }] of byKey) {
      const eq = m.get(t)
      if (eq !== undefined) row[key] = Number(((eq / base0) * 100).toFixed(3))
    }
    return row
  })
  return { rows, base }
}
